/**
 * 1.8.3 (D-1.8.3-6) — an outing's standby list on the office's side (Volunteers → Companion & Wellness → Group outings).
 *
 * The volunteers who said "Yes, add me" after the outing had the volunteers it needs, in the order they joined, and
 * one press — "Offer to the next on standby" — when a place is open: one email to that volunteer, who confirms by
 * their own link. Nothing moves by the press. An outing nobody is waiting for shows nothing here.
 * A volunteer's name only — an outing never carries a tenant list.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OFFER_NEXT_WORDS } from '@shared/standby';
import type { OutingView } from '@shared/volunteers-api';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { offerOutingStandbyReq } from '@/features/events/api183e';
import { errorText } from '@/features/people/api';
import { formatInstant } from '@/lib/dates';
import { useVolunteerRefresh } from './api';

export function OutingStandby183({ outing, lang }: { outing: OutingView; lang: 'en' | 'es' }) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useVolunteerRefresh();
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ text: string; bad: boolean } | null>(null);
  const waiting = outing.interested.filter((i) => i.standby);
  if (!waiting.length) return null;
  const id = outing.eventId;
  const open = outing.standbyOpen ?? 0;
  const next = waiting.find((w) => w.volunteerId === outing.standbyNext) ?? null;
  const blocked = open <= 0 ? 'no-place' : !next ? 'all-offered' : null;
  const offer = async () => {
    setBusy(true);
    try {
      const r = await offerOutingStandbyReq(id);
      setSaid({ text: r.message, bad: false });
      toast(r.message);
      announce(r.message);
    } catch (x) {
      const m = errorText(x);
      setSaid({ text: m, bad: true });
      toast(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
      refresh();
    }
  };
  return (
    <div className="b183e-outing" data-testid={`outing-standby-${id}`}>
      <span className="eyebrow" id={`outing-standby-h-${id}`}>
        {OFFER_NEXT_WORDS[lang].title} · {OFFER_NEXT_WORDS[lang].order}
      </span>
      <ol className="b183e-standby" aria-label={t('standby183.outing.list', { name: outing.name })} data-testid={`outing-standby-list-${id}`}>
        {waiting.map((w) => (
          <li key={w.volunteerId} data-testid={`outing-standby-${id}-${w.volunteerId}`}>
            <span>
              <b>{w.name}</b>
              {w.at ? ` · ${t('standby183.joined', { when: formatInstant(w.at, lang) })}` : ''}
              {w.offeredAt ? ` · ${t('standby183.offered', { when: formatInstant(w.offeredAt, lang) })}` : ''}
            </span>
          </li>
        ))}
      </ol>
      {open > 0 ? (
        <span className="b183e-open" data-testid={`outing-standby-open-${id}`}>
          <Icon name="volunteers" width={14} height={14} /> {t('standby183.open', { count: open })}
        </span>
      ) : null}
      <div className="chips dense b183e-acts">
        <Button variant="secondary" size="sm" disabled={busy || !!blocked} onClick={() => offer()} aria-describedby={`outing-offer-help-${id}`} aria-label={`${OFFER_NEXT_WORDS[lang].button} — ${outing.name}`} data-testid={`outing-offer-next-${id}`}>
          <Icon name="mail" /> {OFFER_NEXT_WORDS[lang].button}
        </Button>
      </div>
      <span className="help" id={`outing-offer-help-${id}`} data-testid={`outing-offer-help-${id}`}>
        {blocked ? t(`standby183.block.${blocked}`) : t('standby183.offerHelp', { name: next?.name ?? '' })}
      </span>
      {said ? (
        <span className={said.bad ? 'field-err' : 'help'} data-testid={`outing-offer-said-${id}`}>
          {said.text}
        </span>
      ) : null}
    </div>
  );
}
